import { useEffect, useState } from 'react';

import { useSearchParams } from 'react-router-dom';

import { BsCheckCircle, BsXCircle } from 'react-icons/bs';

import Card from '#globalComponents/Card/Card';
import FullscreenLayout from '#layouts/FullscreenLayout';

const STATUS_VIEWS = {
	success: {
		title: 'Erfolgreich verbunden!',
		icon: BsCheckCircle,
		chip: 'bg-success-200 text-success-700',
	},
	error: {
		title: 'Verbindung fehlgeschlagen',
		icon: BsXCircle,
		chip: 'bg-warning-200 text-warning-700',
	},
};

const ProcessingIndicator = () => (
	<div className="flex h-9 w-9 items-center justify-center">
		<div className="h-6 w-6 animate-spin rounded-full border-2 border-primary-500 border-t-transparent" />
	</div>
);

const OAuthCallbackPage = () => {
	const [searchParams] = useSearchParams();
	const [status, setStatus] = useState('processing');
	const [message, setMessage] = useState('OAuth-Autorisierung wird verarbeitet...');

	useEffect(() => {
		const handleCallback = () => {
			// Check if we have an error parameter
			const error = searchParams.get('error');
			const errorDescription = searchParams.get('error_description');

			if (error) {
				setStatus('error');
				setMessage(errorDescription || `Fehler bei der Autorisierung: ${error}`);

				// Notify parent window of error
				if (window.opener) {
					window.opener.postMessage(
						{
							type: 'oauth-error',
							error: errorDescription || error,
						},
						window.location.origin,
					);
				}

				// Close window after delay
				setTimeout(() => {
					window.close();
				}, 3000);
				return;
			}

			// Check for success - the backend should have already processed the callback
			// and created/updated the email account. We just need to notify the parent.
			const success = searchParams.get('success');
			const reauthorized = searchParams.get('reauthorized');

			if (success === 'true') {
				setStatus('success');
				// Different message for reauthorization vs new account
				setMessage(
					reauthorized === 'true'
						? 'OAuth-Token erfolgreich erneuert!'
						: 'E-Mail Konto erfolgreich verbunden!',
				);

				// Notify parent window of success
				if (window.opener) {
					window.opener.postMessage(
						{
							type: 'oauth-success',
						},
						window.location.origin,
					);
				}

				// Close window after short delay
				setTimeout(() => {
					window.close();
				}, 1500);
			} else {
				// No clear success/error - assume processing or redirect loop
				setStatus('processing');
				setMessage('Verarbeite Autorisierung...');

				// Try to close after timeout
				setTimeout(() => {
					if (window.opener) {
						window.opener.postMessage(
							{
								type: 'oauth-success',
							},
							window.location.origin,
						);
					}
					window.close();
				}, 2000);
			}
		};

		handleCallback();
	}, [searchParams]);

	const view = STATUS_VIEWS[status];
	const StatusIcon = view?.icon;

	return (
		<FullscreenLayout width="w-full max-w-md px-4">
			<Card className="flex flex-col items-center gap-3 p-6 text-center">
				{view ? (
					<div className={`flex h-9 w-9 items-center justify-center rounded-control ${view.chip}`}>
						<StatusIcon className="h-4 w-4" />
					</div>
				) : (
					<ProcessingIndicator />
				)}
				<div>
					<h2 className="text-sm font-semibold text-text">{view?.title ?? 'Verarbeite Autorisierung'}</h2>
					<p className="mt-1 text-sm text-bgBase-800">{message}</p>
					{view && (
						<p className="mt-2 text-xs text-bgBase-800">Dieses Fenster wird automatisch geschlossen …</p>
					)}
				</div>
			</Card>
		</FullscreenLayout>
	);
};

export default OAuthCallbackPage;
